<script>
// @ts-check

import LocaleSwitcher from '@/components/LocaleSwitcher.vue'
import NavigationLink from '@/components/NavigationLink.vue'
import ProfileSummary from '@/components/ProfileSummary.vue'
import SessionExpirationDialog from '@/components/SessionExpirationDialog.vue'
import ServiceLogo from '@/components/base/ServiceLogo.vue'
import BusinessAccountInformationDialog from '@/components/business-account/BusinessAccountInformationDialog.vue'
import EntriesListTitle from '@/components/entries/EntriesListTitle.vue'
import BottomNav from '@/components/mobile/nav/BottomNav.vue'
import DemoModeInformer from '@/components/registration/DemoModeInformer.vue'
import { userPilotLink } from '@/core/constants'
import { featureAvailable, featureEnabled } from '@/helpers/functions'
import { HIDE_TITLE_ON_MOBILE, ROUTE_NAME } from '@/router/meta'
import { UKKOTURVA_ROUTE_ROOT } from '@/router/ukkoturvaRoutes'
import { mapGetters, mapMutations, mapState } from 'vuex'

export default {
  name: 'BaseView',
  components: {
    SessionExpirationDialog,
    DemoModeInformer,
    LocaleSwitcher,
    NavigationLink,
    ProfileSummary,
    ServiceLogo,
    BusinessAccountInformationDialog,
    BottomNav,
    EntriesListTitle
  },
  data: () => ({
    sidebarOpen: false
  }),
  computed: {
    ROUTE_NAME: () => ROUTE_NAME,
    ...mapState('companyData', ['company']),
    ...mapState('utils', ['isBusinessAccountInformationDialogOpen']),
    ...mapGetters('companyData', [
      'companyIsActive',
      'isWsConnected',
      'isSwanConnected',
      'isInDemoMode',
      'isAdmin',
      'features'
    ]),
    ...mapGetters('app', ['isSandbox', 'isTlpService', 'isLg']),
    ...mapState('app', ['service', 'googleTagManager']),
    ...mapGetters('companyData', ['isCourier']),
    ...mapGetters('bankTransactions', ['selectedBankTransactions']),
    ...mapGetters('mobileData', [
      'isMobileWrapperUser',
      'mobileWrapperPlatform',
      'mobileWrapperVersion'
    ]),
    hasBankConnectionEnabled() {
      return (
        this.isWsConnected ||
        this.isSwanConnected ||
        featureEnabled(this.features, 'psd2_connection')
      )
    },
    shouldShowDemoInformer() {
      if (!this.isInDemoMode) {
        return false
      }

      return !this.$route.name?.match('setup*|dashboard*|company-founding*')
    },
    siteTitle() {
      let title = null

      if (this.$route.matched.length > 0) {
        title = this.$route.matched[0].meta[ROUTE_NAME]

        if (this.isTlpService && title === 'incomes') {
          title = 'invoices'
        }
      }

      return title
    },
    isBankTransactionActionBarOpen() {
      return (
        this.selectedBankTransactions.length &&
        this.siteTitle === 'bank_transactions'
      )
    },
    showMobileBottomNav() {
      const routeName = this.$route.meta?.[ROUTE_NAME]
      return (
        !this.isAdmin &&
        !this.isLg &&
        !this.isBankTransactionActionBarOpen &&
        !routeName?.match(
          'setup*|company-founding*|new_*|edit_*|accept-terms-of-service*'
        )
      )
    },
    isEntriesListPage() {
      return (
        this.siteTitle === 'incomes' ||
        this.siteTitle === 'expenses' ||
        this.siteTitle === 'bank_transactions'
      )
    },
    hideTitleOnMobile() {
      return (
        this.$route.matched.length > 0 &&
        this.$route.matched[0].meta &&
        this.$route.matched[0].meta[HIDE_TITLE_ON_MOBILE]
      )
    },
    isOnSettingsPage() {
      return this.$route.matched.some((route) => {
        return route.meta[ROUTE_NAME] === 'settings'
      })
    },
    routes() {
      if (this.isAdmin) {
        return [
          this.$router.options.routes?.find(
            (route) => route.meta?.[ROUTE_NAME] === 'admin'
          )
        ]
      }

      let routes = this.$router.options.routes?.filter((route) => {
        return route.meta?.showInMainNav === true
      })

      // Don't show bank transaction page link if bank connection is not enabled
      if (!this.hasBankConnectionEnabled) {
        routes = routes?.filter(
          (route) => route.meta?.[ROUTE_NAME] !== 'bank_transactions'
        )
      }

      // Show courier page to couriers and hide driving logs from them
      const hiddenRoute = this.isCourier ? 'driving_logs' : 'courier'
      routes = routes?.filter(
        (router) => router.meta?.[ROUTE_NAME] !== hiddenRoute
      )

      if (!featureAvailable(this.features, 'ukko_assurance')) {
        routes = routes?.filter((route) => {
          return route.meta?.[ROUTE_NAME] !== UKKOTURVA_ROUTE_ROOT.name
        })
      }

      return routes
    },
    showInactiveAlert() {
      return (
        this.company !== null &&
        !this.companyIsActive &&
        this.$route.meta?.[ROUTE_NAME] !== 'dashboard'
      )
    }
  },
  methods: {
    ...mapMutations('utils', ['hideBusinessAccountAdvertisementDialog']),
    closeBusinessAcccountAdDialog() {
      this.hideBusinessAccountAdvertisementDialog()
    },
    showUserpilot() {
      const serviceSpecificLink = userPilotLink[this.service.toUpperCase()]
      window?.userpilot?.trigger(serviceSpecificLink)
    }
  }
}
</script>

<template>
  <div
    class="flex h-full min-h-full overflow-hidden bg-background leading-normal"
    @keydown.escape="sidebarOpen = false"
  >
    <slot name="containers" />
    <!-- Off-canvas menu for mobile -->
    <div v-show="sidebarOpen" class="lg:hidden">
      <Transition
        enter-active-class="transition-opacity duration-300 ease-linear"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-300 ease-linear"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-show="sidebarOpen"
          class="fixed inset-0 z-20"
          tabindex="0"
          @click="sidebarOpen = false"
          @keypress.enter="sidebarOpen = false"
        >
          <div class="absolute inset-0 bg-secondary-600 opacity-75"></div>
        </div>
      </Transition>
      <div class="fixed inset-0 z-20 flex">
        <Transition
          enter-active-class="transition-all duration-300 ease-in-out"
          enter-from-class="-translate-x-full"
          enter-to-class="translate-x-0"
          leave-active-class="transition-all duration-300 ease-in-out"
          leave-from-class="translate-x-0"
          leave-to-class="-translate-x-full"
        >
          <div
            v-show="sidebarOpen"
            class="relative flex w-full max-w-xs min-w-64 flex-1 flex-col bg-secondary pt-5 pb-4"
          >
            <div class="absolute top-0 right-0 p-1">
              <button
                v-show="sidebarOpen"
                class="flex size-12 items-center justify-center rounded-full focus:bg-secondary-600 focus:outline-hidden"
                :aria-label="$t('general.close')"
                type="button"
                @click="sidebarOpen = false"
              >
                <svg
                  class="size-6 text-white"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div class="flex shrink-0 items-center">
              <RouterLink
                :to="{ name: 'dashboard' }"
                class="flex flex-auto flex-col items-center justify-center"
                @click="sidebarOpen = false"
              >
                <ServiceLogo white />
              </RouterLink>
            </div>
            <div class="mt-5 h-0 flex-1 overflow-y-auto">
              <nav>
                <NavigationLink
                  v-for="route in routes"
                  :key="`route-${route.name}`"
                  :route
                  @click="sidebarOpen = false"
                />
              </nav>
            </div>
            <div
              v-if="isMobileWrapperUser && mobileWrapperVersion"
              v-tooltip="mobileWrapperPlatform"
              class="flex flex-initial items-center justify-center"
            >
              <span class="text-center text-sm text-white">
                v{{ mobileWrapperVersion }}
              </span>
            </div>
          </div>
        </Transition>
        <div
          class="flex-1 shrink-0"
          tabindex="0"
          @click="sidebarOpen = false"
          @keypress.enter="sidebarOpen = false"
        >
          <!-- Force sidebar to shrink to fit close icon -->
        </div>
      </div>
    </div>

    <!-- Static sidebar for desktop -->
    <div class="hidden lg:flex lg:shrink-0">
      <div class="flex w-64 flex-col">
        <div class="flex shrink-0 items-center justify-center bg-secondary">
          <RouterLink
            class="mx-10 my-6 flex flex-auto flex-col items-center justify-center"
            :to="{ name: 'dashboard' }"
            @click="sidebarOpen = false"
          >
            <ServiceLogo large white />
          </RouterLink>
        </div>
        <div class="flex h-0 flex-1 flex-col overflow-y-auto">
          <!-- Sidebar component, swap this element with another sidebar if you like -->
          <nav class="flex-1 bg-secondary">
            <NavigationLink
              v-for="route in routes"
              :key="`route-${route.name}`"
              :route
            />

            <!-- Userpilot link -->
            <a
              v-if="googleTagManager"
              class="navigation-link mt-6 cursor-pointer text-secondary-300 hover:bg-secondary-600 focus:bg-secondary-600"
              @click.prevent="showUserpilot"
            >
              <VIcon
                icon="$help-circle-outline"
                class="mr-6 text-secondary-300 transition duration-150 ease-in-out group-hover:text-secondary-300 group-focus:text-secondary-300"
              />
              {{ $t('dashboard.need_help') }}
            </a>
          </nav>
        </div>
        <div
          v-if="isMobileWrapperUser && mobileWrapperVersion"
          v-tooltip="mobileWrapperPlatform"
          class="flex flex-initial items-center justify-center bg-secondary pb-2"
        >
          <span class="text-center text-sm text-white">
            v{{ mobileWrapperVersion }}
          </span>
        </div>
      </div>
    </div>

    <!-- Dynamic topbar -->
    <div class="flex w-0 flex-1 flex-col overflow-hidden">
      <div
        class="relative z-10 flex h-16 shrink-0 flex-row justify-between bg-white px-4 shadow-sm sm:px-6 md:px-8 lg:h-20 lg:px-12"
      >
        <!-- Mobile menu icon -->
        <div class="flex w-24 lg:hidden">
          <button
            type="button"
            class="-ml-4 inline-flex h-full items-center justify-center px-4 leading-none text-primary focus:bg-secondary-100 focus:text-secondary-600 focus:outline-hidden sm:-ml-6 md:-ml-8"
            :aria-label="$t('general.open')"
            @click.stop="sidebarOpen = true"
          >
            <VIcon icon="$menu" />
          </button>
        </div>

        <div class="flex items-center">
          <!-- Desktop title -->
          <h1
            class="hidden font-primary text-2xl font-medium text-secondary-800 lg:block"
          >
            {{ $te(`routes.${siteTitle}`) ? $t(`routes.${siteTitle}`) : '' }}
          </h1>

          <!-- Mobile logo -->
          <RouterLink
            :to="{ name: 'dashboard' }"
            class="inline-block lg:hidden"
            @click="sidebarOpen = false"
          >
            <ServiceLogo plain-logo xsmall class="text-primary" />
          </RouterLink>
        </div>

        <!-- Language and profile menu -->
        <div class="flex w-24 items-center justify-end">
          <LocaleSwitcher class="z-30 mr-4" />
          <ProfileSummary />
        </div>
      </div>
      <main
        id="main-view"
        class="relative z-0 max-w-full flex-1 overflow-x-hidden overflow-y-auto pt-6 focus:outline-hidden lg:pb-6"
        :class="{ 'pb-20': showMobileBottomNav }"
        tabindex="0"
      >
        <!-- Bottom nav -->
        <BottomNav
          v-if="!isLg"
          class="lg:hidden"
          :delay="isMobileWrapperUser"
          :disabled="!showMobileBottomNav"
          :bank-connections-enabled="hasBankConnectionEnabled"
        />

        <!-- Mobile title -->
        <div
          v-if="!hideTitleOnMobile"
          class="mx-auto max-w-6xl px-4 pb-4 lg:hidden lg:px-16"
        >
          <!-- title with info icon link for incomes, expenses & bank_transactions -->
          <EntriesListTitle
            v-if="isEntriesListPage"
            :entry-module="siteTitle"
            tag="h1"
          />

          <h1
            v-else
            class="font-primary text-xl font-medium text-secondary-800"
          >
            {{ $te(`routes.${siteTitle}`) ? $t(`routes.${siteTitle}`) : '' }}
          </h1>
        </div>

        <div class="mx-auto max-w-6xl px-4">
          <div
            v-show="showInactiveAlert"
            class="mb-4 rounded-sm border-y border-r border-l-8 border-danger p-4"
          >
            <p>
              <span class="font-medium text-danger">
                {{ $t('alert.inactive.general.title') }}
              </span>
              {{ $t('alert.inactive.general.description') }}
              <a
                target="_blank"
                rel="noopener noreferrer"
                :href="$t('alert.inactive.general.faq_link')"
                class="inline-flex items-center fill-current text-danger hover:underline"
              >
                {{ $t('alert.inactive.general.link') }}
                <VIcon icon="$arrow-right" size="small" />
              </a>
            </p>
          </div>
          <div
            v-if="isSandbox"
            class="mb-4 w-full rounded-sm border border-primary bg-primary-light p-4"
          >
            <h5 class="mb-2 text-lg font-medium">
              {{ $t('sandbox.warning.title') }}
            </h5>
            <p>
              {{ $t('sandbox.warning.info') }}
            </p>
          </div>

          <div class="bg-transparent">
            <DemoModeInformer
              v-if="isInDemoMode"
              v-show="shouldShowDemoInformer"
            >
              <p>{{ $t('registration_flow.notification.info_generic2') }}</p>
              <template #link>
                {{ $t('registration_flow.notification.action_generic2') }}
              </template>
            </DemoModeInformer>
            <slot />
          </div>
        </div>
      </main>
    </div>

    <SessionExpirationDialog />
    <KeepAlive>
      <BusinessAccountInformationDialog
        v-if="isBusinessAccountInformationDialogOpen"
      />
    </KeepAlive>
  </div>
</template>
